import { ReactNode } from 'react';

import { mergeClasses } from '@/utils';

import { CloseIcon } from '@/assets/icons';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/common';

interface IBaseModal {
  open: boolean;
  onOpen: (open: boolean) => void;
  title: string;
  subtitle?: ReactNode;
  children?: ReactNode;

  isFooterHidden?: boolean;
  isCloseButtonHidden?: boolean;

  isOkButtonHidden?: boolean;
  okButtonLabel?: string;
  onOk?: () => void;
  okButtonClassName?: string;
  isOkButtonDisabled?: boolean;

  isCancelButtonHidden?: boolean;
  cancelButtonLabel?: string;
  onCancel?: () => void;
  cancelButtonClassName?: string;
  isCancelButtonDisabled?: boolean;

  // Customization
  contentClassName?: string;
}

export const BaseModal = ({
  open,
  onOpen,
  title,
  subtitle,
  children,
  isFooterHidden = false,
  isCloseButtonHidden = false,
  isOkButtonHidden = false,
  okButtonLabel = 'Continue',
  onOk,
  okButtonClassName,
  isOkButtonDisabled = false,
  isCancelButtonHidden = false,
  cancelButtonLabel = 'Cancel',
  onCancel,
  cancelButtonClassName,
  isCancelButtonDisabled = false,
  contentClassName,
}: IBaseModal) => {
  return (
    <Dialog open={open} onOpenChange={onOpen}>
      <DialogContent
        showCloseButton={false}
        onInteractOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(e) => e.preventDefault()}
        className={mergeClasses(
          'flex flex-col rounded-2xl p-6 bg-white w-[480px]',
          contentClassName,
        )}
      >
        <DialogHeader className="w-full flex !flex-row items-start justify-between">
          <div className="flex flex-col gap-4 w-full">
            <div className="flex gap-6">
              <DialogTitle className="flex-1 text-brand-primary-darkest !text-[22px] !font-bold !leading-[27px]">
                {title}
              </DialogTitle>
              {!isCloseButtonHidden && (
                <CloseIcon
                  className="w-6 h-6 text-neutral-n6 cursor-pointer hover:text-neutral-n7 transition-colors"
                  onClick={() => onOpen(false)}
                />
              )}
            </div>
            {subtitle && (
              <span className="!text-sm !text-neutral-n7 !font-medium !leading-5">{subtitle}</span>
            )}
          </div>
        </DialogHeader>

        {children && <div className="flex-1 w-full">{children}</div>}

        {!isFooterHidden && (
          <DialogFooter className="w-full gap-3 mt-2 flex !flex-row items-center justify-end">
            {!isCancelButtonHidden && (
              <Button
                variant="cancel"
                disabled={isCancelButtonDisabled}
                className={mergeClasses(
                  'bg-white border border-neutral-n5 rounded-lg min-w-[104px] h-12 !text-base font-semibold',
                  cancelButtonClassName,
                )}
                onClick={onCancel || (() => onOpen(false))}
              >
                {cancelButtonLabel}
              </Button>
            )}
            {!isOkButtonHidden && (
              <Button
                variant="primary"
                disabled={isOkButtonDisabled}
                className={mergeClasses(
                  'rounded-lg min-w-[104px] h-12 !text-base font-semibold',
                  okButtonClassName,
                )}
                onClick={onOk}
              >
                {okButtonLabel}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
